/**
 * Générateur — Automatismes : dans un repère orthogonal, lire les coordonnées
 * d'un point donné (1236) ; placer un point de coordonnées données (1237).
 * Attendu ID : 1236 | Niveau : 3ème | Domaine : Espace et géométrie, Repérage
 * Rubrique : AUTOMATISME (programme 2025, cycle 4 l. 1064-1066 ; mêmes mots
 * que l'automatisme 4e l. 985-987, sans gen en base : ce gen sert les deux).
 * UN FICHIER, DEUX ATTENDUS (base du prof du 13/09, modèle 1213/1246) : les
 * clés lire_* portent l'attendu 1236, les clés placer_* et croix_* l'attendu
 * 1237 (« quelle lettre marque (2 ; −3) ? » est un geste de placement) ;
 * gen_1237 est un dispatch sur ces clés (genererParmi).
 *
 * ── Périmètre (cadrage programme-maths 2026-09-12, mode auto) ─────────────
 * EXTRACTION de gen_945 (objectif 5e) : lire (UN point par saisie, QCM à
 * coordonnées échangées), placer (DEUX points dont un sur un axe), la variante
 * croix du doc (« quelle lettre marque (2 ; −3) ? »). Zones du plan et
 * symétries écartées (objectifs, pas automatisme). Repère ±5 / ±6 au pas 1,
 * demi-entiers une fois sur trois (repère ±3 au pas 0,5 ; À ARBITRER).
 *
 * ── Plan de cartes ─────────────────────────────────────────────────────────
 *   Carte 1 « Lire les coordonnées » : saisie « A( ⬚ ; ⬚ ) »
 *     (un point, sur un axe une fois sur deux) ; QCM [(2 ; −3) ; (−3 ; 2)
 *     échange ; (−2 ; −3) ; (2 ; 3)].
 *   Carte 2 « Placer des points » (placer_points_2d) : deux points dont un
 *     sur un axe.
 *   Carte 3 « Quelle lettre ? » (croix_qcm_unique) : quatre croix m, n, p, r
 *     en (a ; b), (b ; a), (−a ; b), (a ; −b).
 *
 * INVARIANTS : dixièmes entiers ; figures avec data-repere / data-points ;
 * lettres des croix en MINUSCULES (une bonne_reponse d'une lettre A-H serait
 * lue comme un index de choix) ; jamais deux points au même endroit.
 * SACRIFIÉ (fiche « D'abord je marche, ensuite je monte ») : (2 ; −3), exclu
 * des points affichés, des clés et des leurres ; auto-testé.
 *
 * ── Erreurs (familles nommées ; aucun item DEPP) ─────────────────────────
 *   echange_xy     : (−3 ; 2) pour (2 ; −3), PRINCIPALE (945 « erreur n°1 », y compris sur un axe) ;
 *   signe_inverse  : (−2 ; −3), (2 ; 3) ;
 *   arrondi        : (2 ; −3) pour (2,5 ; −3) (demis) ;
 *   decale         : (3 ; −3) (en complément).
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeTexteTrou, basePayload,
} from '../../../utils';
import { imgElement } from '../../5eme/statistiques/svgStats';

const ATTENDU_ID = 1236;
/** Lire = 1236, placer = 1237 (base du prof, 13/09). */
export const ATTENDU = { lire: 1236, placer: 1237 };
const opts = (attendu, label) => ({ niveau: '3eme', matiere: 'maths', attendu, label });

const L_LIRE = 'Lire les coordonnées';
const L_PLACER = 'Placer des points';
const L_CROIX = 'Quelle lettre ?';

/** Le couple sacrifié, en dixièmes : (2 ; −3). */
export const SACRIFIE = { xD: 20, yD: -30 };
const estSacrifie = (p) => p.xD === SACRIFIE.xD && p.yD === SACRIFIE.yD;

const frD = (d) => { const a = Math.abs(d); const s = a % 10 === 0 ? String(a / 10) : `${Math.floor(a / 10)},${a % 10}`; return d < 0 ? `−${s}` : s; };
const saisieD = (d) => frD(d).replace('−', '-');
const paire = (p) => `(${frD(p.xD)} ; ${frD(p.yD)})`;
const NOMS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'K', 'L', 'M'];
const LETTRES_CROIX = ['m', 'n', 'p', 'r', 's', 't', 'u', 'v'];

const EXPLICATIONS = {
    regle: 'D\'abord je marche (l\'abscisse, sur l\'axe horizontal), ensuite je monte ou je descends (l\'ordonnée) : (2 ; −3), c\'est 2 vers la droite puis 3 vers le bas.',
    echange_xy: 'L\'abscisse se lit en premier, sur l\'axe horizontal ; l\'ordonnée en second, sur l\'axe vertical.',
    signe_inverse: 'À gauche de l\'axe vertical, l\'abscisse est négative ; sous l\'axe horizontal, l\'ordonnée est négative.',
    arrondi: 'Le point est entre deux graduations : sa coordonnée est un demi (2,5), pas l\'entier voisin.',
    decale: 'Je recompte les carreaux depuis l\'origine.',
    sur_axe: 'Un point sur l\'axe horizontal a pour ordonnée 0 ; sur l\'axe vertical, son abscisse est 0.',
};

// ─── Régimes et tirages (dixièmes entiers) ────────────────────────────────

const tirerRegime = () => (randInt(1, 3) === 1 ? { nom: 'demis', bD: 30, pasD: 5 } : { nom: 'entiers', bD: randomChoice([50, 60]), pasD: 10 });
const kMax = (reg) => Math.floor(reg.bD / reg.pasD) - 1;
const coord = (reg) => randomChoice([-1, 1]) * randInt(1, kMax(reg)) * reg.pasD;
const cle = (p) => `${p.xD};${p.yD}`;

/** Un point de quadrant (coordonnées non nulles), jamais le sacrifié ni un point déjà pris ; en demis, un demi au moins. */
const tirerQuadrant = (reg, pris) => {
    for (let essai = 0; essai < 200; essai++) {
        const p = { xD: coord(reg), yD: coord(reg) };
        if (estSacrifie(p) || pris.has(cle(p))) continue;
        if (reg.nom === 'demis' && p.xD % 10 === 0 && p.yD % 10 === 0) continue;
        pris.add(cle(p));
        return p;
    }
    return null;
};
/** Un point sur un axe (entier, jamais l'origine). */
const tirerSurAxe = (reg, pris, axe = randomChoice(['x', 'y'])) => {
    for (let essai = 0; essai < 100; essai++) {
        const k = randomChoice([-1, 1]) * randInt(1, Math.floor(reg.bD / 10) - 1) * 10;
        const p = axe === 'x' ? { xD: k, yD: 0 } : { xD: 0, yD: k };
        if (pris.has(cle(p))) continue;
        pris.add(cle(p));
        return p;
    }
    return null;
};

// ─── Figure : repère orthogonal (data-repere / data-points pour les solveurs) ──

const svgRepere = (reg, points) => {
    const PAD = 30; const PLOT = 360; const W = PLOT + 2 * PAD; const { bD } = reg;
    const px = (xD) => Math.round((PAD + ((xD + bD) / (2 * bD)) * PLOT) * 10) / 10;
    const py = (yD) => Math.round((PAD + ((bD - yD) / (2 * bD)) * PLOT) * 10) / 10;
    const x0 = px(0); const y0 = py(0);
    const out = [];
    out.push(`<rect x="0" y="0" width="${W}" height="${W}" fill="white"/>`);
    for (let v = -bD; v <= bD; v += reg.pasD) {
        if (v === 0) continue;
        out.push(`<line x1="${px(v)}" y1="${py(-bD)}" x2="${px(v)}" y2="${py(bD)}" stroke="#e2e8f0" stroke-width="1"/>`);
        out.push(`<line x1="${px(-bD)}" y1="${py(v)}" x2="${px(bD)}" y2="${py(v)}" stroke="#e2e8f0" stroke-width="1"/>`);
    }
    out.push(`<line x1="${PAD - 8}" y1="${y0}" x2="${PAD + PLOT + 10}" y2="${y0}" stroke="#334155" stroke-width="1.6"/>`);
    out.push(`<polygon points="${PAD + PLOT + 16},${y0} ${PAD + PLOT + 7},${y0 - 4} ${PAD + PLOT + 7},${y0 + 4}" fill="#334155"/>`);
    out.push(`<line x1="${x0}" y1="${PAD + PLOT + 8}" x2="${x0}" y2="${PAD - 10}" stroke="#334155" stroke-width="1.6"/>`);
    out.push(`<polygon points="${x0},${PAD - 16} ${x0 - 4},${PAD - 7} ${x0 + 4},${PAD - 7}" fill="#334155"/>`);
    for (let v = -bD; v <= bD; v += 10) {
        if (v === 0) continue;
        out.push(`<line x1="${px(v)}" y1="${y0 - 4}" x2="${px(v)}" y2="${y0 + 4}" stroke="#334155" stroke-width="1"/>`);
        out.push(`<text x="${px(v)}" y="${y0 + 17}" text-anchor="middle" font-size="12" fill="#1e293b">${frD(v)}</text>`);
        out.push(`<line x1="${x0 - 4}" y1="${py(v)}" x2="${x0 + 4}" y2="${py(v)}" stroke="#334155" stroke-width="1"/>`);
        out.push(`<text x="${x0 - 7}" y="${py(v) + 4}" text-anchor="end" font-size="12" fill="#1e293b">${frD(v)}</text>`);
    }
    out.push(`<text x="${x0 - 7}" y="${y0 + 17}" text-anchor="end" font-size="12" fill="#1e293b">0</text>`);
    points.forEach((p) => {
        const cx = px(p.xD); const cy = py(p.yD);
        if (p.croix) out.push(`<g><line x1="${cx - 5}" y1="${cy - 5}" x2="${cx + 5}" y2="${cy + 5}" stroke="#1e3a8a" stroke-width="2"/><line x1="${cx - 5}" y1="${cy + 5}" x2="${cx + 5}" y2="${cy - 5}" stroke="#1e3a8a" stroke-width="2"/><text x="${cx + 8}" y="${cy - 8}" font-size="15" font-weight="700" fill="#1e3a8a">${p.nom}</text></g>`);
        else out.push(`<g><circle cx="${cx}" cy="${cy}" r="4.5" fill="#1e3a8a"/><text x="${cx + 8}" y="${cy - 8}" font-size="15" font-weight="700" fill="#1e3a8a">${p.nom}</text></g>`);
    });
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${W}" width="${W}" height="${W}" font-family="Arial" data-repere="${bD / 10};${reg.pasD / 10}" data-points="${points.map((p) => `${p.nom}:${p.xD / 10},${p.yD / 10}`).join('|')}">${out.join('')}</svg>`;
};

/** Cinq points nommés : un sur chaque axe, trois de quadrants. */
const tirerLecture = (reg) => {
    const pris = new Set();
    const pts = [tirerSurAxe(reg, pris, 'x'), tirerSurAxe(reg, pris, 'y'), tirerQuadrant(reg, pris), tirerQuadrant(reg, pris), tirerQuadrant(reg, pris)];
    if (pts.some((p) => !p)) return null;
    const noms = shuffle(NOMS).slice(0, 5);
    return shuffle(pts.map((p, i) => ({ ...p, nom: noms[i] })));
};

// ─── Carte 1a : lire (saisie, un point) ──────────────────────────────────

const genLireSaisie = () => {
    for (let essai = 0; essai < 50; essai++) {
        const reg = tirerRegime();
        const pts = tirerLecture(reg);
        if (!pts) continue;
        const surAxe = randInt(0, 1) === 1;
        const cands = pts.filter((p) => (surAxe ? p.xD === 0 || p.yD === 0 : p.xD !== 0 && p.yD !== 0 && (reg.nom !== 'demis' || p.xD % 10 !== 0 || p.yD % 10 !== 0)));
        if (!cands.length) continue;
        const P = randomChoice(cands);
        // « A( ⬚ ; ⬚ ) » : l'ordre abscisse puis ordonnée fait partie du geste (retour prof 13/09)
        const phrase = `${P.nom}( {{${saisieD(P.xD)}}} ; {{${saisieD(P.yD)}}} )`;
        const payload = makeTexteTrou(phrase, `<p><b>Lis les coordonnées du point ${P.nom}.</b> (Écris chaque nombre sans le signe + : 3 ou -2,5)</p>`, { ...opts(ATTENDU.lire, L_LIRE), mode: 'saisie', elements_additionnels: imgElement(svgRepere(reg, pts), 'large') });
        payload.contenu = { ...payload.contenu, sousType: surAxe ? 'sur_axe' : reg.nom, explications: { regle: EXPLICATIONS.regle, sur_axe: EXPLICATIONS.sur_axe } };
        payload._meta.params = { regime: reg.nom, bD: reg.bD, points: pts.map((p) => [p.xD, p.yD]), point: [P.xD, P.yD] };
        return payload;
    }
    throw new Error('gen_1236 : saisie sans tirage');
};

// ─── Carte 1b : lire (QCM à coordonnées échangées) ───────────────────────

const leurres = (P, reg) => {
    const c = [];
    const pousser = (code, q) => { if (!(q.xD === P.xD && q.yD === P.yD) && !estSacrifie(q) && Math.abs(q.xD) < reg.bD && Math.abs(q.yD) < reg.bD && !c.some((e) => e.q.xD === q.xD && e.q.yD === q.yD)) c.push({ code, q }); };
    pousser('echange_xy', { xD: P.yD, yD: P.xD });
    pousser('signe_inverse', { xD: -P.xD, yD: P.yD });
    pousser('signe_inverse', { xD: P.xD, yD: -P.yD });
    if (reg.nom === 'demis') pousser('arrondi', { xD: Math.round(P.xD / 10) * 10, yD: Math.round(P.yD / 10) * 10 });
    pousser('decale', { xD: P.xD + reg.pasD, yD: P.yD });
    pousser('decale', { xD: P.xD, yD: P.yD + reg.pasD });
    return c;
};

const genLireQcm = () => {
    for (let essai = 0; essai < 100; essai++) {
        const reg = tirerRegime();
        const pts = tirerLecture(reg);
        if (!pts) continue;
        const surAxe = randInt(1, 3) === 1;
        const cands = pts.filter((p) => (surAxe ? p.xD === 0 || p.yD === 0 : p.xD !== 0 && p.yD !== 0 && (reg.nom !== 'demis' || p.xD % 10 !== 0 || p.yD % 10 !== 0)));
        if (!cands.length) continue;
        const P = randomChoice(cands);
        const tous = leurres(P, reg);
        const echange = tous.find((e) => e.code === 'echange_xy');
        if (!echange) continue;
        const faux = [echange];
        for (const e of shuffle(tous.filter((e) => e !== echange))) if (faux.length < 3 && !faux.some((f) => f.code === e.code && e.code !== 'signe_inverse')) faux.push(e);
        if (faux.length < 3) continue;
        const bon = paire(P);
        const choix = [bon, ...faux.map((f) => paire(f.q))];
        if (new Set(choix).size !== 4) continue;
        const payload = makeQcm(choix, bon, `<p><b>Quelles sont les coordonnées du point ${P.nom} ?</b></p>`, { ...opts(ATTENDU.lire, L_LIRE), elements_additionnels: imgElement(svgRepere(reg, pts), 'large') });
        payload.contenu = { ...payload.contenu, sousType: surAxe ? 'sur_axe' : reg.nom, explications: EXPLICATIONS };
        payload._meta.params = { regime: reg.nom, bD: reg.bD, points: pts.map((p) => [p.xD, p.yD]), point: [P.xD, P.yD], distracteurs: faux.map(({ code }) => ({ code })) };
        return payload;
    }
    throw new Error('gen_1236 : QCM sans tirage');
};

// ─── Carte 2 : placer deux points (interactif) ───────────────────────────

const genPlacer = () => {
    const b = randomChoice([5, 6]); const reg = { nom: 'entiers', bD: b * 10, pasD: 10 };
    const pris = new Set();
    const pts = shuffle([tirerSurAxe(reg, pris), tirerQuadrant(reg, pris)]);
    const noms = shuffle(NOMS).slice(0, 2);
    const bonne = {};
    pts.forEach((p, i) => { bonne[noms[i]] = `${saisieD(p.xD)};${saisieD(p.yD)}`; });
    const liste = pts.map((p, i) => `${noms[i]}${paire(p)}`).join(' et ');
    const payload = basePayload('association', `<p><b>Place les points ${liste} dans le repère.</b></p>`, {
        subType: 'placer_points_2d',
        repere: { xMin: -b, yMin: -b, xMax: b, yMax: b, xStep: 1, yStep: 1 },
        pointsToPlace: noms, bonne_reponse: JSON.stringify(bonne),
        // Le point fantôme n'affiche pas ses coordonnées : placer un point EST le savoir évalué,
        // le texte « (x ; y) » sous le curseur donnait la réponse (demande prof 2026-09-18, §9bis).
        masquerCoordonnees: true,
    }, opts(ATTENDU.placer, L_PLACER));
    payload.contenu = { ...payload.contenu, explications: { regle: EXPLICATIONS.regle, sur_axe: EXPLICATIONS.sur_axe } };
    payload._meta.params = { b, points: pts.map((p) => [p.xD, p.yD]) };
    return payload;
};

// ─── Carte 3 : quelle lettre marque (a ; b) ? (QCM, croix) ───────────────

const genCroixQcm = () => {
    for (let essai = 0; essai < 100; essai++) {
        const reg = tirerRegime();
        const P = tirerQuadrant(reg, new Set());
        if (!P || Math.abs(P.xD) === Math.abs(P.yD)) continue;       // (a ; b), (b ; a), (−a ; b), (a ; −b) distinctes
        const croix = [P, { xD: P.yD, yD: P.xD }, { xD: -P.xD, yD: P.yD }, { xD: P.xD, yD: -P.yD }];
        if (croix.some(estSacrifie)) continue;
        const lettres = shuffle(LETTRES_CROIX).slice(0, 4);
        const pts = croix.map((q, i) => ({ ...q, nom: lettres[i], croix: true }));
        const payload = makeQcm(shuffle([...lettres]), lettres[0], `<p><b>Quelle lettre marque le point de coordonnées ${paire(P)} ?</b></p>`, { ...opts(ATTENDU.placer, L_CROIX), elements_additionnels: imgElement(svgRepere(reg, shuffle(pts)), 'large') });
        payload.contenu = { ...payload.contenu, sousType: reg.nom, explications: EXPLICATIONS };
        payload._meta.params = { regime: reg.nom, bD: reg.bD, point: [P.xD, P.yD], distracteurs: [{ code: 'echange_xy' }, { code: 'signe_inverse' }, { code: 'signe_inverse' }] };
        return payload;
    }
    throw new Error('gen_1236 : croix sans tirage');
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'dixièmes entiers ; repère ±5 / ±6 au pas 1, demis (±3 au pas 0,5) une fois sur trois ; un point sur un axe interrogé une fois sur deux en saisie, une sur trois au QCM',
        'sacrifié (2 ; −3) exclu des points affichés, des clés et des leurres',
    ],
    lire_qcm_unique: 'echange_xy toujours présente (la principale), puis signe_inverse (deux formes), arrondi (demis), decale',
    croix_qcm_unique: 'les quatre croix (a ; b), (b ; a) échange, (−a ; b) et (a ; −b) signes ; lettres minuscules',
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    lire_texte_trou_saisie: genLireSaisie,
    lire_qcm_unique: genLireQcm,
    placer_placer_points_2d: genPlacer,
    croix_qcm_unique: genCroixQcm,
};

/** 1236 : lire. */
export const CATEGORIES = [
    { id: 'lire', label: L_LIRE, formatsList: [{ key: 'lire_texte_trou_saisie', label: 'Saisie' }, { key: 'lire_qcm_unique', label: 'QCM' }] },
];
/** 1237 : placer (gen_1237). */
export const CATEGORIES_PLACER = [
    { id: 'placer', label: L_PLACER, formatsList: [{ key: 'placer_placer_points_2d', label: 'Placer' }] },
    { id: 'croix', label: L_CROIX, formatsList: [{ key: 'croix_qcm_unique', label: 'QCM' }] },
];
const clesDe = (cats) => cats.flatMap((c) => c.formatsList.map(({ key }) => key));

/** Tire parmi les clés données (dispatch des deux gens). */
export const genererParmi = (cles, typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = cles.filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(cles);
    } else key = randomChoice(cles);
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};

export const generate = (typeExercice) => genererParmi(clesDe(CATEGORIES), typeExercice);
export { ATTENDU_ID };
